import { useRef, useEffect, useState } from 'react';

const features = [
  {
    tag: 'Patented VEN Technology',
    title: '특허받은 VEN 기술',
    icon: '⬡',
    points: [
      '1 마이크론 이하 초미세 입자로 흡수율 극대화',
      '특수 에너지 활성화 공정을 통한 효능 증대',
      '특허 기술로 지올라이트의 해독 효과 최적화',
    ],
    accent: '#8B7355',
    bg: '#F5F0E8',
  },
  {
    tag: 'FAZOS Certified',
    title: 'FAZOS 테스트 인정',
    icon: '✦',
    points: [
      '독립적인 연구소를 통한 유해물질 제거력 입증',
      '제3자 공인 기관의 객관적 검증 완료',
      '유럽 기준 엄격한 품질 테스트 통과',
    ],
    accent: '#6B8C6B',
    bg: '#EFF5EF',
  },
  {
    tag: 'Premium Quality',
    title: '최고 품질의 원료',
    icon: '◈',
    points: [
      '고순도 지올라이트와 자연 유래 성분만 사용',
      '합성 첨가물 · 인공 방부제 일절 배제',
      '크로아티아 청정 지역 직접 채굴 및 정제',
    ],
    accent: '#7A6E5F',
    bg: '#F8F5F0',
  },
];

export default function BrandStorySection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="brand" className="bg-[#FAFAF8] py-24 px-6" ref={sectionRef}>
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-[#8B7355] text-xs font-medium tracking-[0.3em] uppercase mb-3">Brand Story</p>
          <h2 className="text-[#2C2825] text-4xl lg:text-5xl font-light mb-4">
            자연에서 찾은 삶의 본질
          </h2>
        </div>

        {/* Croatia Origin Block */}
        <div id="brand-croatia" className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-20 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="w-full h-80 rounded-2xl overflow-hidden">
            <img
              src="https://public.readdy.ai/ai/img_res/d4f17c4ea3dad9010321cf58b7b50efd.jpg"
              alt="크로아티아 청정 자연환경"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div>
            <p className="text-[#8B7355] text-xs tracking-[0.3em] uppercase mb-3">Croatia Origin</p>
            <h3 className="text-[#2C2825] text-3xl font-light mb-5 leading-snug">
              유럽의 보석,<br />크로아티아 청정환경 기반
            </h3>
            <p className="text-[#6B5F52] text-sm leading-relaxed mb-4">
              유럽의 보석으로 불리는 크로아티아의 순수한 자연환경에서 영감을 받아 탄생한 브랜드입니다. 수백만 년에 걸쳐 형성된 청정 광물 자원을 바탕으로, 최고 품질의 웰니스 제품을 제공합니다.
            </p>
            <p className="text-[#6B5F52] text-sm leading-relaxed">
              아드리아해 연안의 청정한 자연이 만들어낸 고순도 지올라이트는 오직 이곳에서만 발견되는 특별한 자원입니다. 우리는 그 자연의 힘을 그대로 전달합니다.
            </p>
            <div className="mt-8 pt-6 border-t border-[#E8E0D4]">
              <p className="text-[#6B5F52] text-sm leading-relaxed">
                ZEO 지오지올라이트는 유럽 공인 광산에서 직접 채굴한 원료를 전문 시설에서 첨단 세정 및 활성화 공정을 통해 정제합니다. 엄격한 ISO 및 EU 품질 기준을 충족하는지 독립 기관의 검증을 거쳐, 높은 순도와 효과적인 독소 제거 성능을 보장합니다.
              </p>
            </div>
          </div>
        </div>

        {/* Product Lineup Block */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-20 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div>
            <p className="text-[#8B7355] text-xs tracking-[0.3em] uppercase mb-3">Product Lineup</p>
            <h3 className="text-[#2C2825] text-3xl font-light mb-5 leading-snug">
              세 가지 용량,<br />하나의 철학
            </h3>
            <p className="text-[#6B5F52] text-sm leading-relaxed mb-4">
              처음 시작하는 분부터 온 가족이 함께 사용하는 분까지, 각자의 라이프스타일에 맞는 용량을 선택할 수 있도록 세 가지 라인업으로 구성했습니다.
            </p>
            <p className="text-[#6B5F52] text-sm leading-relaxed mb-8">
              용량이 달라져도 원료와 품질 기준은 동일합니다. 100% 천연 크로아티아 지올라이트, 그 본질은 변하지 않습니다.
            </p>
            <div className="flex flex-col gap-4">
              {[
                { size: '120g', desc: '입문용 · 여행 · 체험', color: '#C4A882' },
                { size: '350g', desc: '베스트셀러 · 일상 루틴', color: '#8B7355' },
                { size: '1,000g', desc: '대용량 · 가족용 · 절약', color: '#2C2825' },
              ].map((item) => (
                <div key={item.size} className="flex items-center gap-4">
                  <div
                    className="w-1.5 h-10 rounded-full flex-shrink-0"
                    style={{ backgroundColor: item.color }}
                  />
                  <div>
                    <p className="text-[#2C2825] text-base font-medium">{item.size}</p>
                    <p className="text-[#8B7355] text-xs tracking-wide">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="w-full h-96 rounded-2xl overflow-hidden bg-[#F5F2EE] flex items-center justify-center">
            <img
              src="https://public.readdy.ai/ai/img_res/cff0b1e9-2daa-486a-84aa-2fc117465778.png"
              alt="ZEO 지올라이트 제품 라인업 120g 350g 1000g"
              className="w-full h-full object-contain p-6"
            />
          </div>
        </div>

        {/* Divider */}
        <div className={`flex items-center gap-4 mb-16 transition-all duration-700 delay-200 ${visible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex-1 h-px bg-[#E8E0D4]" />
          <p className="text-[#8B7355] text-xs tracking-[0.25em] uppercase whitespace-nowrap">What Makes Us Different</p>
          <div className="flex-1 h-px bg-[#E8E0D4]" />
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <div
              key={f.tag}
              id={
                f.tag === 'Patented VEN Technology'
                  ? 'brand-ven'
                  : f.tag === 'FAZOS Certified'
                  ? 'brand-certification'
                  : undefined
              }
              className={`rounded-2xl p-8 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ backgroundColor: f.bg, transitionDelay: `${300 + i * 120}ms` }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-full text-lg"
                  style={{ backgroundColor: f.accent + '22', color: f.accent }}
                >
                  {f.icon}
                </div>
                <span
                  className="text-[10px] font-semibold tracking-[0.2em] uppercase"
                  style={{ color: f.accent }}
                >
                  {f.tag}
                </span>
              </div>
              <h4
                className="text-[#2C2825] text-xl font-light mb-5"
              >
                {f.title}
              </h4>
              <ul className="space-y-3">
                {f.points.map((p) => (
                  <li key={p} className="flex items-start gap-2.5">
                    <span className="mt-1 w-1 h-1 rounded-full flex-shrink-0" style={{ backgroundColor: f.accent, marginTop: '6px' }} />
                    <p className="text-[#6B5F52] text-sm leading-relaxed">{p}</p>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
